import { useEffect, useRef, useState } from 'react';
import { Ghost } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * Always-visible "who's here" panel for live sessions. Renders the
 * teacher and the student side by side; each row swaps to a Ghost
 * icon when the participant's tab is away. Under the student row a
 * "Ghosted ×N" counter ticks up on every away→present transition
 * (server-side increment wired in a follow-on commit; until then this
 * shows 0 if the prop isn't populated).
 *
 * Mounted on both ends — same props shape, same visual. Today only
 * the student broadcasts away_state (teacher's usePresenceAway passes
 * broadcastAway: false), so in practice only the student's row ever
 * goes ghost; the panel is intentionally symmetric so wiring the
 * teacher side later is a one-prop change.
 *
 * Draggable from the small header bar. Default position: top: 16,
 * right: 16. Once dragged we switch to absolute left/top so the panel
 * stays where the user put it across re-renders. The drag listeners
 * attach to `window` (not the header) so a fast mouse leaving the
 * header mid-drag doesn't lose the grip.
 *
 * Props:
 *   participants    — [{ id, full_name, initials, role, away, ghost_count }]
 *   currentUserId   — used to mark the local participant with "(you)"
 */
export default function PresencePanel({ participants = [], currentUserId }) {
  // Default top: 72 clears the 56px top navbar (h-14 on both the
  // student's StudentLiveTopBar and TeacherShell's header) with a
  // ~16px gap below it. Drag overrides this once the user moves it.
  const [position, setPosition] = useState({ top: 72, right: 16, left: null });
  const dragStateRef = useRef(null);
  const panelRef = useRef(null);

  // Sort so teacher is on top, student below.
  const ordered = [...participants].sort((a, b) => {
    if (a.role === b.role) return 0;
    return a.role === 'teacher' ? -1 : 1;
  });

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      const w = panelRef.current?.offsetWidth ?? 200;
      const h = panelRef.current?.offsetHeight ?? 100;
      const left = Math.max(
        4,
        Math.min(window.innerWidth - w - 4, e.clientX - drag.offsetX)
      );
      const top = Math.max(
        4,
        Math.min(window.innerHeight - h - 4, e.clientY - drag.offsetY)
      );
      setPosition({ top, left, right: null });
    };
    const onUp = () => {
      dragStateRef.current = null;
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const onHeaderMouseDown = (e) => {
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragStateRef.current = {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    document.body.style.userSelect = 'none';
    e.preventDefault();
  };

  if (ordered.length === 0) return null;

  const style = position.left == null
    ? { top: position.top, right: position.right }
    : { top: position.top, left: position.left };

  return (
    <div
      ref={panelRef}
      className={cn(
        'fixed z-50 select-none',
        'rounded-lg border border-gray-200 dark:border-gray-700',
        'bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm',
        'shadow-lg w-52'
      )}
      style={style}
    >
      <div
        onMouseDown={onHeaderMouseDown}
        className={cn(
          'flex items-center justify-between px-2.5 py-1 cursor-move',
          'border-b border-gray-100 dark:border-gray-800',
          'text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500'
        )}
        title="Drag to move"
      >
        <span>Who's here</span>
        <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">⋮⋮</span>
      </div>
      <ul className="py-1.5 px-2 space-y-1.5">
        {ordered.map((p) => (
          <PresenceRow
            key={p.id}
            participant={p}
            isSelf={p.id === currentUserId}
          />
        ))}
      </ul>
    </div>
  );
}

function PresenceRow({ participant, isSelf }) {
  const away = !!participant.away;
  const isStudent = participant.role === 'student';
  const ghostCount = participant.ghost_count ?? 0;

  return (
    <li className="flex items-start gap-2">
      <div
        className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
          'text-[11px] font-medium',
          away
            ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
            : 'bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 text-gray-700 dark:text-gray-200'
        )}
        title={away ? `${participant.full_name} is away` : participant.full_name}
      >
        {away ? (
          <Ghost className="w-3.5 h-3.5" strokeWidth={2} />
        ) : (
          participant.initials || participant.full_name?.[0] || '?'
        )}
      </div>
      <div className="flex-1 min-w-0 leading-tight">
        <div className="text-xs font-medium text-gray-900 dark:text-white truncate">
          {participant.full_name}
          {participant.role === 'teacher' && (
            <span
              className={cn(
                'ml-1.5 align-middle px-1.5 py-0.5 rounded-full',
                'bg-indigo-100 text-indigo-700',
                'dark:bg-indigo-500/20 dark:text-indigo-300',
                'text-[9px] font-semibold uppercase tracking-wide leading-none'
              )}
            >
              Teacher
            </span>
          )}
          {isSelf && (
            <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">
              (you)
            </span>
          )}
        </div>
        {isStudent && ghostCount > 0 && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 mt-1 px-1.5 py-0.5 rounded-full',
              'bg-gray-100 text-gray-600',
              'dark:bg-gray-800 dark:text-gray-400',
              'text-[10px] font-medium leading-none'
            )}
            title={`Tabbed away ${ghostCount} time${ghostCount === 1 ? '' : 's'} this session`}
          >
            <Ghost className="w-3 h-3" strokeWidth={2} aria-hidden="true" />
            Ghosted ×{ghostCount}
          </span>
        )}
      </div>
    </li>
  );
}
